// Central site data & constants for Pro DJ's & Event Services
export const QUOTE_URL = "https://prodjseventservices.djintelligence.com/quote";
export const CLIENT_PORTAL_URL = "https://prodjseventservices.djintelligence.com/";

export const CONTACT = {
  phone: "325.305.6135",
  phoneHref: "tel:+13253056135",
  smsHref: "sms:+13253056135",
  // Public-facing email is obfuscated via <ObfuscatedEmail /> to keep the plaintext out of the initial HTML.
  // Backend + admin dashboard still use it directly.
  emailUser: "Weddings",
  emailDomain: "ProDJsAbilene.com",
  email: "Weddings@ProDJsAbilene.com",
  emailHref: "mailto:Weddings@ProDJsAbilene.com",
  handle: "@prodjsabilene",
  area: "Abilene & The Surrounding Big Country",
  address: {
    street: "21 Crossroads Dr",
    city: "Abilene",
    region: "TX",
    postalCode: "79605",
    country: "US",
    full: "21 Crossroads Dr, Abilene, TX 79605",
  },
};

export const REVIEW_URL = "https://g.page/r/CQ3yAzKOfHfiEBM/review";
export const GOOGLE_BUSINESS_URL = "https://maps.app.goo.gl/jKTsUH8QN7Yn5CfT7";

export const SOCIALS = [
  { label: "Google Business", href: GOOGLE_BUSINESS_URL, icon: "google" },
  { label: "Instagram", href: "https://www.instagram.com/prodjsabilene", icon: "instagram" },
  { label: "Facebook", href: "https://www.facebook.com/prodjsabilene", icon: "facebook" },
  { label: "Pinterest", href: "https://www.pinterest.com/prodjsabilene", icon: "pin" },
  { label: "YouTube", href: "https://www.youtube.com/channel/UCY1IQ-pcnHviFaQQb-o_dVg/featured", icon: "youtube" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/prodjsabilene", icon: "linkedin" },
  { label: "X", href: "https://x.com/prodjsabilene", icon: "x" },
];

// Third-party trust badges surfaced across the site (footer + trust band)
export const TRUST_BADGES = [
  {
    label: "#1 Best DJ Service in Abilene 2026",
    provider: "Quality Business Awards",
    href: "https://qualitybusinessawards.com/2026/the-best-DJ-Service-in-Abilene-Texas/Pro-DJs-Event-Services",
    testid: "badge-quality-business-awards",
  },
  {
    label: "Top 3 Entertainment Companies in Abilene",
    provider: "Three Best Rated",
    href: "https://threebestrated.com/entertainment-companies-in-abilene-tx",
    testid: "badge-three-best-rated",
  },
];

// Local, optimized logos (down from 365 KiB / 341 KiB / 214 KiB → ~50 KiB / ~45 KiB / ~6 KiB)
export const LOGO_DARK = "/logo-dark.webp";
export const LOGO_LIGHT = "/logo-light.webp";
export const LOGO = LOGO_DARK;

// w=1000&q=65 balances retina sharpness (~1.6x for 600px tiles) against transfer size (PageSpeed image-delivery audit).
const U = (id, w = 1000) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=65`;

// Responsive srcset for Unsplash images so mobile devices fetch a right-sized file
// instead of the full 1000px asset (PageSpeed "improve image delivery" audit).
const UNSPLASH_WIDTHS = [400, 500, 640, 800, 1000, 1200];
export const imgSrcSet = (url) =>
  UNSPLASH_WIDTHS.map((w) => `${url.replace(/w=\d+/, `w=${w}`)} ${w}w`).join(", ");

export const IMAGES = {
  hero: U("photo-1519741497674-611481863552"),
  heroAlt: U("photo-1511285560929-80b456fea0bc"),
  reception1: U("photo-1767050190883-29d644fa5b99"),
  reception2: U("photo-1530367086713-83fee2b59d84"),
  dj1: U("photo-1618409698966-6caa2b95733a"),
  dj2: U("photo-1470225620780-dba8ba36b745"),
  decor: U("photo-1469371670807-013ccf25f16a"),
  couple: U("photo-1722805740128-b01efd09b4b7"),
  florals: U("photo-1522673607200-164d1b6ce486"),
  table: U("photo-1478146059778-26028b07395a"),
};

// Rotating hero slides — real Pro DJ's Abilene wedding photos.
const ASSETS = "https://customer-assets-wrfwihn1.emergentagent.net/job_event-harmony-6/artifacts";
export const HERO_SLIDES = [
  { src: `/hero/sparklers.webp`, srcMobile: `/hero/sparklers-800.webp`, alt: "Bride and groom sparkler send-off at an Abilene wedding by Pro DJ's, Abilene wedding DJs" },
  { src: `/hero/dance-dip.webp`, alt: "Bride and groom mid-dip on the dance floor at an Abilene wedding DJ'd by Pro DJ's" },
  { src: `/hero/first-dance.webp`, alt: "First dance surrounded by cheering guests — Abilene wedding DJ entertainment by Pro DJ's" },
  { src: `/hero/marquee-lights.webp`, alt: "Bride and groom dancing by marquee light letters at an Abilene wedding reception" },
  { src: `/hero/wedding-party.webp`, alt: "Groom performing with the wedding party at a Pro DJ's Abilene wedding reception" },
];

export const NAV_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export const SERVICES = [
  {
    key: "dj",
    number: "01",
    title: "Abilene Wedding DJ Services",
    tagline: "The best DJ in Abilene for your reception",
    body: "With over 20 years behind the decks, our Abilene wedding DJ team reads the room and keeps the floor full — seamless mixing, thoughtful MC work, and a soundtrack curated entirely around your love story.",
    features: ["Professional MC & announcements", "Custom playlist curation", "Premium sound & wireless mics", "Ceremony, cocktail & reception audio"],
    image: IMAGES.dj1,
    alt: "Abilene wedding DJ from Pro DJ's mixing at an elegant wedding reception",
  },
  {
    key: "coordination",
    number: "02",
    title: "Wedding Coordinating & Planning",
    tagline: "Abilene wedding planning, effortlessly handled",
    body: "From the first walk-through to the final send-off, our Abilene wedding planning and coordinating team choreographs your timeline so you can be fully present. Vendors managed, moments protected, nothing left to chance.",
    features: ["Day-of timeline design", "Vendor management", "Rehearsal direction", "On-site coordination team"],
    image: IMAGES.couple,
    alt: "Bride and groom celebrating a perfectly coordinated Abilene wedding by Pro DJ's",
  },
  {
    key: "decor",
    number: "03",
    title: "Wedding Decorator & Event Decor",
    tagline: "Elegance you can feel",
    body: "Trained under a Texas Master Florist, our wedding decorator concierge designs spaces that sparkle — florals, linens, lighting and layered textures composed into one cohesive, breathtaking look.",
    features: ["Floral & centerpiece design", "Ambient & up-lighting", "Linens, drapery & tablescapes", "Full room styling"],
    image: IMAGES.decor,
    alt: "Wedding decorator tablescape with candlelight and florals at an Abilene wedding",
  },
  {
    key: "events",
    number: "04",
    title: "Event Coordination",
    tagline: "Beyond the aisle",
    body: "Corporate galas, festivals, milestone celebrations — we bring the same polish and precision to every occasion in the Big Country, start to standing ovation.",
    features: ["Corporate & private events", "Festivals & large productions", "Sound & entertainment", "Full production management"],
    image: IMAGES.reception1,
    alt: "Event reception coordinated by Pro DJ's, Abilene wedding DJs and event team",
  },
];

export const TEAM = [
  {
    name: "David McDonald",
    role: "Event Concierge",
    bio: "David's taste can only be described in one word — elegant. Wedding & event decor is his specialty, and having trained under a Texas Master Florist, he makes anything he touches sparkle, all while coordinating your event.",
    image: IMAGES.florals,
  },
  {
    name: "Kody Mac",
    role: "Wedding DJ & MC",
    bio: "With over 20 years of experience, Kody is among the best of Abilene's wedding DJ & MCs, having performed at hundreds of events, corporate parties, and festivals — becoming a highly sought after entertainer in the Big Country.",
    image: IMAGES.dj2,
  },
];

export const EVENT_TYPES = ["Wedding", "Corporate Event", "Private Party", "Festival", "Quinceañera", "Other"];
export const SERVICE_OPTIONS = ["Wedding DJ", "Wedding Coordination", "Event Decor", "Event Coordination"];
